/* eslint-disable max-statements */
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { toast } from 'react-toastify';
import { FileUpload } from 'primereact/fileupload';
import { Tag } from 'primereact/tag';
import { Tooltip } from 'primereact/tooltip';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { TabPanel, TabView } from 'primereact/tabview';
import warningImage from '../../assets/warningImage.png';
import { useCreateApprovalsHaltMutation, useGetApprovalHaltReasonsQuery, useGetScorecardHaltApprovalsQuery } from './haltReinitiateSlice';
import { selectCurrentEmpid, selectCurrentUserFullName } from '../../pages/login/authSlice';
import { ConstApprovalWindowStatus, ConstReasonType } from '../../utils/typeConstants';
import { useGetPrimaryApprovalWindowQuery } from '../../pages/compensation/compensationSlice';
import MessageInfo from '../messageInfo/MessageInfo';
import PrimaryApproval from '../../features/compensation/primaryApproval/PrimaryApproval';

interface HaltReinitiateProps {
    scorecardName?: string;
    inputYear: number;
    inputMonth: number;
    inputScorecardVersion: number;
}

export const HaltReinitiate = ({ inputYear, inputMonth, inputScorecardVersion, scorecardName
}: HaltReinitiateProps) => {
    const [displayWipeOutApprovalModal, setDisplayWipeOutApprovalModal] = useState(false);
    const [displayClosedStatusModel, setDisplayClosedStatusModel] = useState(false);
    const [haltReason, setHaltReason] = useState<number>();
    const loggedUserId = useSelector(selectCurrentEmpid);
    const loggedUser = useSelector(selectCurrentUserFullName)?.split('(')[0];
    const [createApprovalsHalt] = useCreateApprovalsHaltMutation();
    const [scorecardApprovalStatus, setScorecardApprovalStatus] = useState('');
    const [displayError, setDisplayError] = useState(false);
    const fileUploadRef = useRef(null);
    const [fileData, setFileData] = useState<any>([]);
    const [fileName, setFileName] = useState('');
    const [totalSize, setTotalSize] = useState(0);
    const [errorMessage, setErrorMessage] = useState('');
    const [userMessage, setUserMessage] = useState('');
    const [isDialogDisable, setIsDialogDisable] = useState(false);
    const [isApprovalHalted, setIsApprovalHalted] = useState(false);
    const [haltedbyId, setHaltedbyId] = useState(0);
    const [haltedBy, setHaltedby] = useState('');
    const [haltedReason, setHaltedReason] = useState('');
    const [haltDocument, setHaltDocument] = useState('');
    const [documentName, setDocumentName] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [inputReadonly] = useState(false);

    const { data: approvalHaltReasonsData } = useGetApprovalHaltReasonsQuery({
        typeId: ConstReasonType.SCORECARD_APPROVAL_HALT_REASON,
    });
    const { data: scorecardStatus } = useGetPrimaryApprovalWindowQuery(
        {
            year: inputYear,
            month: inputMonth,
            scorecardVersionId: inputScorecardVersion,
            approvalWindowConfigLevelId: 3,
        },
        { skip: !inputYear || !inputMonth || !inputScorecardVersion }
    );
    const { data: ScorecardApprovalHaltData } = useGetScorecardHaltApprovalsQuery({
        year: inputYear,
        month: inputMonth,
        scorecardVersionId: inputScorecardVersion,
    },
        { skip: !inputYear || !inputMonth || !inputScorecardVersion || !isApprovalHalted }
    );

    useEffect(() => {
        setScorecardApprovalStatus('');
        setIsDialogDisable(false);
        setErrorMessage('');
        setHaltReason(undefined);
        setDisplayError(false);
        onTemplateRemove(fileData, () => {
            if (fileUploadRef.current) {
                (fileUploadRef.current as FileUpload | null)?.clear?.();
            }
        });
        setIsApprovalHalted(false);
    }, [inputYear, inputMonth, inputScorecardVersion]);
    const createApprovalHalts = (haltApprovals: any) => {
        createApprovalsHalt(haltApprovals)
            .unwrap()
            .then(() => {
                toast.success('Approvals are Halted and Reinitiated');
            })
            .then((error: any) => {
                if (error) {
                    toast.error('Approvals Halt failed');
                }
            });
        onTemplateRemove(fileData, () => {
            if (fileUploadRef.current) {
                (fileUploadRef.current as FileUpload | null)?.clear?.();
            }
        });
        dialogFuncMap.displayWipeOutApprovalModal(false);
        setHaltReason(undefined);
        setIsDialogDisable(true);
    }
    const onHaltApprovalsClick = () => {
        const haltApprovals = {
            'year': inputYear,
            'month': inputMonth,
            'scorecardVersionId': inputScorecardVersion,
            'halt_by': loggedUserId,
            'halt_reason_id': haltReason,
            'halt_approval_file_name': fileName,
            'halt_approval_file_data': fileData
        }
        createApprovalHalts(haltApprovals);
    }

    useEffect(() => {
        if (scorecardStatus) {
            if (!isDialogDisable) {
                dialogFuncMap.displayClosedStatusModel(true);
            }
            setDisplayError(true);
            setIsApprovalHalted(false);
            // for empty status
            setUserMessage('Approval windows have not begun. Scorecards are being re-calculated automatically.');
            const windowData = scorecardStatus?.data[0];
            if (windowData) {
                setScorecardApprovalStatus(windowData.approval_window_status_name);
                switch (windowData.approval_window_status_id) {
                    case ConstApprovalWindowStatus.CLOSED:
                        setUserMessage('Payout has occurred, halting approvals are not allowed.');
                        break;
                    case ConstApprovalWindowStatus.APPROVALS_NOT_STARTED:
                        setUserMessage('Approval windows have not begun. Scorecards are being re-calculated automatically.');
                        break;
                    case ConstApprovalWindowStatus.READY_FOR_EXTRACT:
                        setUserMessage('Approval windows have closed. Halting approvals are not allowed.');
                        break;
                    case ConstApprovalWindowStatus.APPROVALS_HALTED:
                        setUserMessage('Approval windows are already halted.');
                        setIsApprovalHalted(true);
                        break;
                    default:
                        setDisplayError(false);
                        dialogFuncMap.displayClosedStatusModel(false);
                        break;
                }
            }
        }
    }, [scorecardStatus]);
    useEffect(() => {
        if (ScorecardApprovalHaltData?.data.length > 0) {
            setHaltedbyId(ScorecardApprovalHaltData.data[0].halt_by);
            setHaltedby(ScorecardApprovalHaltData.data[0].halt_by_name);
            setHaltedReason(ScorecardApprovalHaltData.data[0].reason_name);
            setHaltDocument(ScorecardApprovalHaltData.data[0].documentation);
            setDocumentName(ScorecardApprovalHaltData.data[0].document_name);
        }
    }, [ScorecardApprovalHaltData]);

    const downloadImage = (haltDocument: any, documentName: any) => {
        const blob = base64ToBlob(haltDocument);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = documentName || 'image';
        link.click();
        URL.revokeObjectURL(url);
    };

    const base64ToBlob = (base64: any) => {
        const byteCharacters = atob(base64.split(',')[1]);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        return new Blob([new Uint8Array(byteNumbers)], { type: 'application/octet-stream' });
    };

    const dialogFuncMap = {
        displayWipeOutApprovalModal: setDisplayWipeOutApprovalModal,
        displayClosedStatusModel: setDisplayClosedStatusModel,
    };

    const onTemplateSelect = (e: any) => {
        let totalSize = 0;
        totalSize += e.files[0].size;
        setTotalSize(totalSize);
    };

    const onTemplateUpload = (e: any) => {
        let totalSize = 0;
        totalSize += e.files[0].size || 0;
        setTotalSize(totalSize);
    };

    const onTemplateClear = () => {
        setTotalSize(0);
    };

    const chooseOptions = { label: 'Choose', icon: 'pi pi-fw pi-plus', className: 'btn-primary' };
    const uploadOptions = {
        label: 'Upload',
        icon: 'pi pi-upload',
        className: 'btn-primary',
    };
    const cancelOptions = {
        label: 'Cancel',
        icon: 'pi pi-times',
        className: 'btn-danger',
    };
    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };
    const customHandler = async (event: any) => {
        const file = event.files[0];
        setFileName(file.name);
        const reader = new FileReader();
        const blob = await fetch(file.objectURL).then((r) => r.blob()); //blob:url
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
            const base64data = reader.result;
            setFileData(base64data);
            onTemplateUpload({ files: [file] });
        };
        if (haltReason === undefined) {
            setErrorMessage('Please select a reason');
        }
        else {
            setErrorMessage('');
            dialogFuncMap.displayWipeOutApprovalModal(true);
        }
    };
    const itemTemplate = (file: any, props: any) => {
        return (
            <div className="row w-100">
                <div className="col-md-6">
                    <div className="row">
                        <div className="col-md-12">
                            <img
                                alt={file.name}
                                className="me-3"
                                role="presentation"
                                src={file.objectURL}
                                width={300}
                            />
                            {file.name}
                        </div>
                    </div>

                    <div className="row mt-5">
                        <div className="col-md-12">
                            <Tag value={props.formatSize} severity="info" className="px-3 py-2 me-3" />
                            <Button
                                type="button"
                                icon="pi pi-times"
                                className="p-button-outlined p-button-rounded p-button-danger ml-auto"
                                onClick={() => onTemplateRemove(file, props.onRemove)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const emptyTemplate = () => {
        return (
            <div className="flex align-items-center flex-column text-center">
                <i
                    className="pi pi-cloud-upload mt-3 p-5 d-block"
                    style={{
                        fontSize: '5em',
                        borderRadius: '50%',
                        backgroundColor: 'var(--surface-b)',
                        color: '#3F5765',
                    }}
                />
                <span style={{ fontSize: '1.2em', color: 'var(--text-color-secondary)' }} className="my-5">
                    Drag and Drop Image Here
                </span>
            </div>
        );
    };

    const onTemplateRemove = (file: any, callback: any) => {
        setTotalSize(totalSize - file.size);
        callback();
    };

    const onHide = () => {
        dialogFuncMap.displayWipeOutApprovalModal(false);
        dialogFuncMap.displayClosedStatusModel(false);
    };
    if (inputYear && inputMonth && inputScorecardVersion) {
        return (
            <div className="card border-0 p-3 ps-0">
                <div className="card-body p-0">
                    <h3>
                        {scorecardName} <span className="h5 ms-2">{scorecardApprovalStatus}</span>
                    </h3>
                    <h6 className="mt-3">Requestor</h6>
                    <div className="row mt-3">
                        <div className="col-md-8">
                            <div className="row">
                                <div className="col-md-3">
                                    <h6>Oracle ID</h6>
                                    {!isApprovalHalted ? <p>{loggedUserId}</p> : <p>{haltedbyId}</p>}
                                </div>
                                <div className="col-md-3">
                                    <h6>Name</h6>
                                    {!isApprovalHalted ? <p>{loggedUser}</p> : <p>{haltedBy}</p>}
                                </div>
                                <div className="col-md-3">
                                    <h6>Reason</h6>
                                    {!isApprovalHalted ? <Dropdown
                                        className="custom-dropdown w-100"
                                        placeholder="Select Reason"
                                        value={haltReason}
                                        options={approvalHaltReasonsData?.data}
                                        optionValue={'reason_id'}
                                        optionLabel={'reason_name'}
                                        onChange={(e) => setHaltReason(e.value)}
                                    /> :
                                        <p>{haltedReason}</p>}
                                </div>
                                {isApprovalHalted ?
                                    <div className="col-md-3">
                                        <h6>View Attachment</h6>
                                        <Button
                                            className="haltAttachmentViewIcon p-button-outlined border-0"
                                            type="button"
                                            onClick={openModal}
                                        />
                                    </div> : ''}
                                <Dialog
                                    visible={isModalOpen}
                                    onHide={closeModal}
                                >
                                    <img
                                        alt={documentName}
                                        className="me-3"
                                        src={haltDocument}
                                        width={300}
                                    />
                                    <div className="row">
                                        <div className="col-md-12 text-center">
                                            <Button label="Download" icon="pi pi-download" onClick={() => downloadImage(haltDocument, documentName)} className="btn-primary" />
                                        </div>
                                    </div>
                                </Dialog>
                                <span className="error-text" id="errMessage">
                                    {errorMessage}
                                </span>
                            </div>
                        </div>
                    </div>
                    {!isApprovalHalted ?
                        <><h6 className="mt-3">
                            Upload VP Approval <Tooltip target=".help-circle" position="top" />
                            <div className="help-circle d-inline-block" data-pr-tooltip="Screenshot or File">
                                <i className="pi pi-question mt-1 ms-1" style={{ color: 'white' }} />
                            </div>
                        </h6><FileUpload
                                ref={fileUploadRef}
                                name="FileUpload"
                                accept="image/*"
                                maxFileSize={1000000}
                                onUpload={onTemplateUpload}
                                onSelect={onTemplateSelect}
                                onError={onTemplateClear}
                                onClear={onTemplateClear}
                                chooseOptions={chooseOptions}
                                uploadOptions={uploadOptions}
                                cancelOptions={cancelOptions}
                                itemTemplate={itemTemplate}
                                emptyTemplate={emptyTemplate}
                                className="mt-3"
                                uploadHandler={customHandler}
                                customUpload
                                uploadLabel="Halt Approvals"
                                disabled={displayError} />
                            <h6 className="mt-3 text-end">
                                *Scorecard Recalculation is Automatic after data uploads
                            </h6>
                        </> : ''}
                </div>
                {isApprovalHalted ? <div className="card">
                    <TabView>
                        <TabPanel header="Primary Approval">
                            <PrimaryApproval
                                inputYear={inputYear}
                                inputMonth={inputMonth}
                                inputScorecardVersion={inputScorecardVersion}
                                inputReadonly={inputReadonly}
                                isApprovalHalted={isApprovalHalted}
                            />
                        </TabPanel>
                    </TabView>
                </div> : ''}


                <Dialog
                    visible={displayWipeOutApprovalModal}
                    style={{ width: '20vw', textAlign: 'center' }}
                    onHide={() => onHide()}
                    closable={false}
                >
                    <img src={warningImage} alt="warning" />

                    <h3 className="text-danger mt-3">Warning</h3>

                    <p>This will wipe out Agent Approval</p>
                    <div className="row">
                        <div className="col-md-6 text-end">
                            <Button
                                label="Continue"
                                className="btn-primary"
                                onClick={onHaltApprovalsClick}
                            />
                        </div>
                        <div className="col-md-6 text-start">
                            <Button label="Cancel" onClick={() => onHide()} className="btn-secondary" />
                        </div>
                    </div>
                </Dialog>
                <Dialog
                    visible={displayClosedStatusModel}
                    style={{ width: '20vw', textAlign: 'center' }}
                    onHide={() => onHide()}
                    closable={false}
                >
                    <img src={warningImage} alt="warning" />
                    <h3 className="text-danger mt-3">Error</h3>
                    <p>{userMessage}</p>
                    <div className="row">
                        <div className="col-md-12 text-center">
                            <Button label="Cancel" onClick={() => onHide()} className="btn-secondary" />
                        </div>
                    </div>
                </Dialog>
            </div>
        );
    }
    return (
        <div className="col-12">
            <MessageInfo title="No results found."
                message="Try adjusting the month or scorecard filter above."
                rowHeight="208px" />
        </div>
    );
};